/**
 * Page-aware topic detection. The widget reads the page it's embedded on and
 * derives a short topic ("Conversion Intelligence", "Car Accident") so the
 * entry bars can read "Ask me about <topic>". Client-side by design — the
 * widget picks up whatever page it's deployed on, and re-derives on SPA
 * navigation (snippet.ts re-runs this on route change).
 */

/** A short topic phrase for the current page, or null when there isn't a clean
 *  one (e.g. a homepage whose heading is a tagline) → callers use a fallback. */
export function pageTopic(): string | null {
  // Prefer the <title>'s leading segment ("Topic | Brand" → "Topic"), which is
  // usually a clean page name; fall back to the page's main <h1>.
  return clean(leadSegment(document.title)) ?? clean(headingText());
}

/** "Ask me about <topic>" when a topic is found, else the provided fallback. */
export function askAboutCopy(fallback: string): string {
  const topic = pageTopic();
  return topic ? `Ask me about ${topic}` : fallback;
}

function headingText(): string | null {
  return document.querySelector("h1")?.textContent ?? null;
}

/** Leading segment before a brand separator: "A | B" / "A – B" / "A: B" → "A". */
function leadSegment(title: string | undefined): string | undefined {
  return title?.split(/\s[|–—·:-]\s/)[0];
}

/** Keep short topic phrases; reject empty / long / sentence-like (taglines). */
function clean(s: string | null | undefined): string | null {
  if (!s) return null;
  const t = s.replace(/\s+/g, " ").trim();
  if (!t || t.length > 48 || t.split(" ").length > 6 || /[.!?]$/.test(t)) return null;
  return t;
}

/* ---- Placeholder fitting (2026-08-26, Lynda's rail) ----------------------
 * An input placeholder cannot wrap: when "Ask me about Leadership Coaching"
 * met a 340px rail it rendered "Ask me about Leadership Coa" — a mid-word cut
 * that reads as broken, not minimal. These helpers pick copy that FITS.
 * Width heuristic rather than canvas measurement, calibrated on the live
 * break: the 340px rail seated ~27 glyphs of 17px system-ui before cutting —
 * (340 - reserved 96) / 27 ≈ 9px per average glyph. `reservedPx` covers the
 * icon, the send button and padding. Slightly conservative for the capsule's
 * smaller 15px input, which only means it falls back a touch earlier — the
 * graceful direction. Deterministic, so the choice is testable. */

const PLACEHOLDER_AVG_CHAR_PX = 9;
export const PLACEHOLDER_RESERVED_PX = 96;

/** True when `text` should render whole in a field inside a host this wide.
 *  Unknown width (null — observer not landed) counts as fitting: the full
 *  copy is today's behavior and must survive anywhere we cannot measure. */
export function placeholderFits(
  text: string,
  hostWidthPx: number | null,
  reservedPx: number = PLACEHOLDER_RESERVED_PX,
): boolean {
  if (hostWidthPx == null || hostWidthPx <= 0) return true;
  const budget = Math.floor((hostWidthPx - reservedPx) / PLACEHOLDER_AVG_CHAR_PX);
  return text.length <= budget;
}

/** Pure core: `full` when it fits, else `fallback` whole. The fallback is
 *  never fitted — below ~200px nothing helps, and a short generic line
 *  ellipsizing is strictly less broken than a topic cut mid-word. */
export function pickPlaceholder(
  full: string,
  fallback: string,
  hostWidthPx: number | null,
): string {
  if (full === fallback) return fallback;
  return placeholderFits(full, hostWidthPx) ? full : fallback;
}

/** "Ask me about <topic>" when it fits, else the fallback whole. */
export function fittedAskAboutCopy(fallback: string, hostWidthPx: number | null): string {
  return pickPlaceholder(askAboutCopy(fallback), fallback, hostWidthPx);
}
